import type { BindDeclaration, BindTransform } from '../binding-enhancer/types';

const VALID_TRANSFORMS = new Set<BindTransform>([
  'options',
  'text',
  'count',
  'isEmpty',
  'tagLabels',
  'stats',
  'badges',
]);

/**
 * Runtime type guard for {@link BindDeclaration}.
 *
 * A value is a well-formed binding declaration when it is a plain object
 * with a non-empty string `source`, a non-empty string `path`, and either
 * no `transform` or a `transform` drawn from the known set
 * (`options` | `text` | `count` | `isEmpty` | `tagLabels` | `stats` |
 * `badges`).
 *
 * Non-conforming values (strings, numbers, objects with wrong field
 * types, unknown transforms at the type level) return `false` — the
 * walker falls back to template-string interpolation or pass-through
 * per ADR-11.
 */
export function isBindDeclaration(value: unknown): value is BindDeclaration {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  const candidate = value as {
    source?: unknown;
    path?: unknown;
    transform?: unknown;
  };
  if (typeof candidate.source !== 'string' || candidate.source.length === 0) {
    return false;
  }
  if (typeof candidate.path !== 'string' || candidate.path.length === 0) {
    return false;
  }
  if (candidate.transform === undefined) {
    return true;
  }
  return (
    typeof candidate.transform === 'string' &&
    VALID_TRANSFORMS.has(candidate.transform as BindTransform)
  );
}
